import * as Yup from "yup";
import moment from "moment";
import {
  QuotationItemType,
  QuotationSourceType,
} from "features/Quotations/constants/quotation.constants";

function parseValidUntilValue(value: unknown) {
  if (value == null || value === "") return null;
  const s = String(value);
  const primary = moment(s, "DD MMMM ,YYYY", true);
  if (primary.isValid()) return primary;
  const iso = moment(s, "YYYY-MM-DD", true);
  if (iso.isValid()) return iso;
  const loose = moment(s);
  return loose.isValid() ? loose : null;
}

const itemTypes = Object.values(QuotationItemType) as QuotationItemType[];

/** Shared with material request BOQ (same line shape as quotations). */
export const quotationBoqItemSchema = Yup.object({
  id: Yup.number().optional(),
  type: Yup.mixed<QuotationItemType>()
    .oneOf(itemTypes)
    .required("Type is required"),
  description: Yup.string().trim().required("Description is required"),
  productId: Yup.mixed<number | string>().nullable().optional(),
  quantity: Yup.number()
    .typeError("Quantity must be a number")
    .positive("Quantity must be greater than zero")
    .required("Quantity is required"),
  unit: Yup.string().trim().required("Unit is required"),
  rate: Yup.number()
    .typeError("Rate must be a number")
    .min(0, "Rate cannot be negative")
    .required("Rate is required"),
});

export const schema = Yup.object({
  sourceType: Yup.mixed<QuotationSourceType>()
    .oneOf(Object.values(QuotationSourceType))
    .required("Source type is required"),

  title: Yup.string().required("The title field is required"),

  validUntil: Yup.string()
    .required("The valid till field is required")
    .test("valid-until-parse", "Invalid date", (value) => {
      return parseValidUntilValue(value) != null;
    })
    .test(
      "valid-until-after-today",
      "Valid until must be later than today",
      (value) => {
        const parsed = parseValidUntilValue(value);
        if (!parsed) return false;
        const today = moment().startOf("day");
        return parsed.clone().startOf("day").isAfter(today);
      },
    ),

  estimatedStartDate: Yup.date()
    .required("Estimated start date is required")
    .typeError("Invalid start date"),

  estimatedEndDate: Yup.date()
    .required("Estimated end date is required")
    .typeError("Invalid end date")
    .min(
      Yup.ref("estimatedStartDate"),
      "Estimated end date cannot be before start date",
    ),

  description: Yup.string().nullable().optional(),

  advancePaymentPercent: Yup.mixed<number | "">()
    .optional()
    .test(
      "advance-pct-range",
      "Advance payment must be a whole number between 0 and 100",
      (v) =>
        v === "" ||
        v == null ||
        (Number.isInteger(Number(v)) &&
          Number(v) >= 0 &&
          Number(v) <= 100),
    ),

  workCompletionWeeks: Yup.mixed<number | "">()
    .optional()
    .test(
      "work-weeks-range",
      "Work completion weeks must be a whole number between 1 and 999",
      (v) =>
        v === "" ||
        v == null ||
        (Number.isInteger(Number(v)) &&
          Number(v) >= 1 &&
          Number(v) <= 999),
    ),

  items: Yup.array()
    .of(quotationBoqItemSchema)
    .min(1, "Please add at least one line item")
    .required("Please add at least one line item"),

  clientId: Yup.number().required("The client field is required"),

  sourceId: Yup.number().required("The source field is required"),

  isEdit: Yup.boolean().required(),
});

export default schema;
